import { useState, useCallback, useRef, useEffect } from 'react';
import StockService from '../services/stockService';

/**
 * Custom Hook for Stock Data Functionality
 * Manages stock data state and YFinance integration
 */
export const useStockData = (symbol = null, options = {}) => {
    const [stockData, setStockData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    
    // Configuration
    const {
        autoFetch = false,
        fetchInterval = null, // in milliseconds
        retryAttempts = 3,
        retryDelay = 1000,
        dataTypes = ['price', 'history', 'info'] // what data to fetch
    } = options;
    
    // Refs
    const isMountedRef = useRef(true);
    const intervalRef = useRef(null);
    const fetchStockDataRef = useRef(null);
    
    /**
     * Fetch stock price data
     */
    const fetchStockPrice = useCallback(async (stockSymbol = symbol) => {
        if (!stockSymbol) {
            console.warn('useStockData: No symbol provided for price fetch');
            return null;
        }
        
        try {
            console.log(`useStockData: Fetching price for ${stockSymbol}`);
            const data = await StockService.getStockPrice(stockSymbol);
            return data;
        } catch (err) {
            console.error(`useStockData: Price fetch error for ${stockSymbol}`, err);
            throw err;
        }
    }, [symbol]);
    
    /**
     * Fetch stock history data
     */
    const fetchStockHistory = useCallback(async (stockSymbol = symbol, period = '1y') => {
        if (!stockSymbol) {
            console.warn('useStockData: No symbol provided for history fetch');
            return null;
        }
        
        try {
            console.log(`useStockData: Fetching history for ${stockSymbol} with period ${period}`);
            const data = await StockService.getStockHistory(stockSymbol, period);
            return data;
        } catch (err) {
            console.error(`useStockData: History fetch error for ${stockSymbol}`, err);
            throw err;
        }
    }, [symbol]);
    
    /**
     * Fetch stock info data
     */
    const fetchStockInfo = useCallback(async (stockSymbol = symbol) => {
        if (!stockSymbol) {
            console.warn('useStockData: No symbol provided for info fetch');
            return null;
        }
        
        try {
            console.log(`useStockData: Fetching info for ${stockSymbol}`);
            const data = await StockService.getStockInfo(stockSymbol);
            return data;
        } catch (err) {
            console.error(`useStockData: Info fetch error for ${stockSymbol}`, err);
            throw err;
        }
    }, [symbol]);
    
    /**
     * Fetch all stock data
     */
    const fetchAllStockData = useCallback(async (stockSymbol = symbol, period = '1y') => {
        if (!stockSymbol) {
            console.warn('useStockData: No symbol provided for all data fetch');
            return null;
        }
        
        try {
            console.log(`useStockData: Fetching all data for ${stockSymbol}`);
            const data = await StockService.getAllStockData(stockSymbol, period);
            return data;
        } catch (err) {
            console.error(`useStockData: All data fetch error for ${stockSymbol}`, err);
            throw err;
        }
    }, [symbol]);
    
    /**
     * Fetch specific data types
     */
    const fetchStockData = useCallback(async (stockSymbol = symbol, types = dataTypes, period = '1y') => {
        console.log('🚨 fetchStockData FUNCTION CALLED!', { stockSymbol, types, period });
        console.log('🔍 fetchStockData called with:', { stockSymbol, types, period });
        
        if (!stockSymbol) {
            console.warn('useStockData: No symbol provided for data fetch');
            return null;
        }
        
        console.log('✅ Symbol provided, setting loading state');
        setIsLoading(true);
        setError(null);
        
        try {
            console.log(`useStockData: Fetching ${types.join(', ')} data for ${stockSymbol}`);
            
            const promises = [];
            const data = {};
            
            // Prepare promises for requested data types
            if (types.includes('price')) {
                console.log('📊 Adding price promise');
                promises.push(fetchStockPrice(stockSymbol).then(result => { data.price = result; }));
            }
            
            if (types.includes('history')) {
                console.log('📈 Adding history promise');
                promises.push(fetchStockHistory(stockSymbol, period).then(result => { data.history = result; }));
            }
            
            if (types.includes('info')) {
                console.log('ℹ️ Adding info promise');
                promises.push(fetchStockInfo(stockSymbol).then(result => { data.info = result; }));
            }
            
            console.log('⏳ Waiting for all promises to resolve...');
            // Wait for all promises to resolve
            await Promise.all(promises);
            
            console.log('✅ All promises resolved, adding metadata');
            // Add metadata
            data.symbol = stockSymbol;
            data.lastUpdated = new Date().toISOString();
            data.period = period;
            
            console.log('🔍 Checking isMountedRef.current:', isMountedRef.current);
            if (isMountedRef.current) {
                console.log('✅ Component still mounted, setting state');
                setStockData(data);
                setLastUpdated(new Date().toISOString());
                console.log(`useStockData: Successfully fetched data for ${stockSymbol}`, data);
            } else {
                console.log('❌ Component unmounted, not setting state');
                // Try to set state anyway - React will ignore if component is unmounted
                try {
                    setStockData(data);
                    setLastUpdated(new Date().toISOString());
                    console.log(`useStockData: Attempted to set state even though unmounted for ${stockSymbol}`, data);
                } catch (e) {
                    console.log('❌ Failed to set state:', e);
                }
            }
            
            return data;
            
        } catch (err) {
            console.error(`useStockData: Error fetching data for ${stockSymbol}`, err);
            
            if (isMountedRef.current) {
                setError(err.message);
            }
            
            throw err;
        } finally {
            console.log('🔍 Finally block - checking isMountedRef.current:', isMountedRef.current);
            if (isMountedRef.current) {
                console.log('✅ Setting loading to false');
                setIsLoading(false);
            }
        }
    }, [symbol, dataTypes, fetchStockPrice, fetchStockHistory, fetchStockInfo]);

    // Store the function in ref
    fetchStockDataRef.current = fetchStockData;
    
    /**
     * Fetch data with retry logic
     */
    const fetchStockDataWithRetry = useCallback(async (stockSymbol = symbol, types = dataTypes, period = '1y') => {
        return await StockService.getStockDataWithRetry(stockSymbol, 'all', retryAttempts, retryDelay);
    }, [symbol, dataTypes, retryAttempts, retryDelay]);
    
    /**
     * Fetch multiple stocks data
     */
    const fetchMultipleStocks = useCallback(async (symbols) => {
        if (!symbols || symbols.length === 0) {
            console.warn('useStockData: No symbols provided for multiple fetch');
            return null;
        }
        
        setIsLoading(true);
        setError(null);
        
        try {
            console.log(`useStockData: Fetching data for multiple stocks: ${symbols}`);
            const data = await StockService.getMultipleStocksData(symbols);
            
            if (isMountedRef.current) {
                setStockData(data);
                setLastUpdated(new Date().toISOString());
                console.log('useStockData: Successfully fetched multiple stocks data', data);
            }
            
            return data;
            
        } catch (err) {
            console.error('useStockData: Error fetching multiple stocks data', err);
            
            if (isMountedRef.current) {
                setError(err.message);
            }
            
            throw err;
        } finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    }, []);
    
    /**
     * Search stocks
     */
    const searchStocks = useCallback(async (query) => {
        if (!query || query.trim() === '') {
            console.warn('useStockData: Empty search query');
            return null;
        }
        
        try {
            console.log(`useStockData: Searching stocks with query: ${query}`);
            const results = await StockService.searchStocks(query);
            return results;
        } catch (err) {
            console.error(`useStockData: Search error for query: ${query}`, err);
            throw err;
        }
    }, []);
    
    /**
     * Clear stock data
     */
    const clearStockData = useCallback(() => {
        console.log('useStockData: Clearing stock data');
        setStockData(null);
        setError(null);
        setLastUpdated(null);
    }, []);
    
    /**
     * Refresh stock data
     */
    const refreshStockData = useCallback(async (stockSymbol = symbol, types = dataTypes, period = '1y') => {
        console.log(`useStockData: Refreshing data for ${stockSymbol}`);
        return await fetchStockData(stockSymbol, types, period);
    }, [symbol, dataTypes, fetchStockData]);
    
    /**
     * Get stock data statistics
     */
    const getStockDataStats = useCallback(() => {
        if (!stockData) {
            return {
                hasData: false,
                dataTypes: [],
                lastUpdated: null,
                symbol: symbol
            };
        }
        
        const dataTypes = [];
        if (stockData.price) dataTypes.push('price');
        if (stockData.history) dataTypes.push('history');
        if (stockData.info) dataTypes.push('info');
        
        return {
            hasData: true,
            dataTypes,
            lastUpdated,
            symbol: stockData.symbol || symbol,
            hasError: !!error,
            isLoading
        };
    }, [stockData, lastUpdated, symbol, error, isLoading]);
    
    /**
     * Export stock data
     */
    const exportStockData = useCallback(() => {
        if (!stockData) {
            console.warn('useStockData: No data to export');
            return;
        }
        
        const exportData = {
            ...stockData,
            exportTime: new Date().toISOString(),
            stats: getStockDataStats()
        };
        
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `stock-data-${stockData.symbol}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        console.log('useStockData: Stock data exported', exportData);
    }, [stockData, getStockDataStats]);
    
    // Auto-fetch effect
    useEffect(() => {
        console.log('🔄 Auto-fetch effect triggered:', { autoFetch, symbol });
        if (autoFetch && symbol && fetchStockDataRef.current) {
            console.log('🚀 Auto-fetch: Calling fetchStockData for symbol:', symbol);
            fetchStockDataRef.current();
        } else {
            console.log('❌ Auto-fetch: Conditions not met:', { autoFetch, symbol, hasRef: !!fetchStockDataRef.current });
        }
    }, [autoFetch, symbol]); // Removed fetchStockData from dependencies
    
    // Auto-refresh interval effect
    useEffect(() => {
        if (fetchInterval && symbol) {
            intervalRef.current = setInterval(() => {
                if (isMountedRef.current) {
                    refreshStockData();
                }
            }, fetchInterval);
        }
        
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [fetchInterval, symbol, refreshStockData]);
    
    // Cleanup on unmount
    useEffect(() => {
        console.log('🔧 useStockData: Component mounted, isMountedRef set to true');
        return () => {
            console.log('🔧 useStockData: Component unmounting, isMountedRef set to false');
            isMountedRef.current = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);
    
    return {
        // State
        stockData,
        isLoading,
        error,
        lastUpdated,
        
        // Actions
        fetchStockData,
        fetchStockDataWithRetry,
        fetchStockPrice,
        fetchStockHistory,
        fetchStockInfo,
        fetchAllStockData,
        fetchMultipleStocks,
        searchStocks,
        refreshStockData,
        clearStockData,
        
        // Utilities
        getStockDataStats,
        exportStockData
    };
}; 